import {computed, effect, inject, Injectable, Signal, signal} from '@angular/core';
import {
  AppointmentSettingType,
  CancellationTokenSource, CaptureError,
  Day,
  debounce,
  Gender,
  IMessageCode,
  OperationCancelledError,
  ReasonType,
  Schema,
  State,
  TimeOfDay
} from "@mindpath/shared";
import {HttpClient} from "@angular/common/http";
import {lastValueFrom} from "rxjs";
import {FinderPage, NavService, Section} from "./nav.service";
import {apiUrl} from "../networking";
import {config} from '../../config';
import {AppService} from "./app.service";
import { UiService } from './ui.service';
import {PiwikService} from "../analytics/piwik";
import type {SpanAttributeValue} from "@sentry/core";
import * as Sentry from "@sentry/angular";

export const GenderIdMapping = {
  [Gender.None]: undefined,
  [Gender.Male]: 1,
  [Gender.Female]: 2,
  [Gender.NonBinary]: 4,
}

export const LanguageIdMapping = {
  'English': 1,
  'Spanish': 2,
  'Hindi': 3,
  'Urdu': 4,
  'Portuguese': 5,
  'Japanese': 6,
  'Chinese': 7,
  'Farsi': 8,
  'French': 9,
  'German': 10,
  'Mandarin': 11,
  'Punjabi/Hindi': 12,
  'Cantonese': 13,
  'Italian': 14,
  'Punjabi': 15,
  'Vietnamese': 16,
} as const;


@Injectable({
  providedIn: 'root'
})
export class FinderService {

  #paging$ = signal<SearchPaging>({page: 1, pageSize: 10});
  #cancellationToken: CancellationTokenSource | null = null;
  #searching$ = signal<boolean>(false);
  readonly searching = this.#searching$.asReadonly();
  #navSvc = inject(NavService);
  #piwik = inject(PiwikService);

  // todo: Switch to v19 resource signal when updated
  #searchResults$ = signal<{
    readonly filterUsed: SearchFilter;
    readonly items: readonly Schema<'ClinicianAvailabilityInfo'>[];
    readonly error?: IMessageCode;
    readonly totalCount: number;
    readonly totalPages: number;
    readonly currentPage: number;
    readonly pageSize: number;
  } | null>(null);

  readonly searchResults: Signal<{
    readonly items: readonly Schema<'ClinicianAvailabilityInfo'>[];
    readonly totalCount: number;
    readonly totalPages: number;
    readonly currentPage: number;
    readonly pageSize: number;
    readonly error?: IMessageCode;
    readonly loading?: boolean;
  } | null> = this.#searchResults$.asReadonly();

  #httpClient = inject(HttpClient);
  #app = inject(AppService);

  #inFinder = computed(() => {
    const current = this.#navSvc.current();
    return current.section === Section.Finder && current.page === FinderPage.Find;
  });

  #filters = computed(() => {
    const data = this.#app.data().filters;
    return data;
  }, {
    equal: (a, b) => {
      const aKeys = Object.keys(a) as (keyof SearchFilter)[];
      const bKeys = Object.keys(b) as (keyof SearchFilter)[];
      if (aKeys.length !== bKeys.length) {
        return false;
      }
      return aKeys.every(key => a[key] === b[key] || key === 'location' && a[key]?.lat === b[key]?.lat && a[key]?.long === b[key]?.long);
    }
  })

  constructor() {

    // on filter change, reset paging
    effect(() => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _ = this.#app.data().filters;
      this.#paging$.set({page: 1, pageSize: 10});
    });


    effect(() => {
      const filter = this.#filters();
      const paging = this.#paging$();
      const inFinder = this.#inFinder();
      const appReady = this.#app.ready();
      if (!appReady) {
        return;
      }
      if (filter.state === State.None) {
        this.#searchResults$.set({
          filterUsed: filter,
          items: [],
          totalCount: 0,
          totalPages: 0,
          currentPage: 0,
          pageSize: 0,
          error: inFinder ? CaptureError({message: 'Please select a state to search for clinicians.'}, {noLog: true}) : undefined,
        });
        return;
      }

      if (inFinder) {
        this.ui.showLoading('Looking for matches...', 'Please wait while we find available clinicians');
      }
      this.#saveFilter().then(() => {
        if (inFinder) {
          this.#applySearch(paging, filter).finally(() => {
            // hide after a short delay to prevent flicker
            setTimeout(() => {
              this.ui.hideLoading();
            }, 100);
          });
        }
      }).catch(() => {
        if (inFinder) {
          // hide after a short delay to prevent flicker
          setTimeout(() => {
            this.ui.hideLoading();
          }, 100);
        }
      });
    });
  }

  ui = inject(UiService);

  reset() {
    this.#searchResults$.set(null);
    this.#paging$.set({page: 1, pageSize: 10});
  }

  changePaging(data: { page: number, pageSize?: number }) {
    this.#paging$.set({...data, pageSize: data.pageSize ?? 10});
  }

  #saveFilter = debounce(async () => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    await lastValueFrom(this.#httpClient.patch<void>(apiUrl`${config}`, this.#app.data().filters)).then(() => {
    });
  }, 150);

  search(filter: SearchFilter) {
    const paging = this.#paging$();
    return this.#applySearch(paging, filter);
  }

  readonly #applySearch = debounce(async (paging: SearchPaging, filter: SearchFilter) => {
    return Sentry.startSpan({
      name: 'Search For Clinicians',
      op: 'finder.search',
      },
      async span => {

      if (!filter.weeksOut) {
        filter = {...filter, weeksOut: 0};
      }
      if (!filter.conditionId) {
        filter = {...filter, conditionId: -1};
      }

      const previousResults = this.#searchResults$();

      // if there are previous results check to see if it's the same page and the filters are the same
      if (previousResults && previousResults.currentPage === (paging.page || 1)) {
        if (previousResults.filterUsed === filter) {
          return;
        }
        const previousKeys = new Set(Object.keys(previousResults.filterUsed)) as Set<keyof SearchFilter>;
        const currentKeys = new Set(Object.keys(filter)) as Set<keyof SearchFilter>;
        // there aren't any keys that are different between the two filters
        if (!previousKeys.symmetricDifference(currentKeys).size) {
          // check if the values are the same. If they are, don't re-fetch
          if (Array.from(previousKeys).every(key => previousResults.filterUsed[key] === filter[key])) {
            span.setStatus({
              code: 0, // 0 = UNSET
              message: 'No changes to filter',
            });
            return;
          }
        }
      }



      try {
        
        this.#searching$.set(true);
        
        // const url = config.endpoints.api;
        const params = new URLSearchParams();
        if (!paging.page || paging.page < 1) {
          params.append('page', '1');
        } else {
          params.append('page', paging.page.toString());
        }

        if (!paging.pageSize || paging.pageSize < 1 || paging.pageSize > 100) {
          params.append('pageSize', '10');
        } else {
          params.append('pageSize', paging.pageSize.toString());
        }

        Object.entries(filter).forEach(([key, value]) => {
          if (value === undefined || value === null) {
            return;
          }
          if (key === 'location') {
            value = `${value.lat},${value.long}`;
          }
          if (typeof value === 'string' && !value.trim().length) {
            return;
          }

          params.append(key, value.toString());
        });

        let cancelSource = this.#cancellationToken;
        if (cancelSource) {
          cancelSource.cancel();
        }
        cancelSource = new CancellationTokenSource();
        this.#cancellationToken = cancelSource;

        span.setAttributes({
          ...Array.from(params.entries()).reduce((acc, [key, value]) => {
            const asNumber = parseInt(value, 10);
            acc[`filter.${key}`] = value.trim() === '' || isNaN(asNumber) ? value : asNumber;
            return acc;
          }, {} as Record<string, SpanAttributeValue>),
        });

        const data = await lastValueFrom(this.#httpClient.get<{
          totalPages: number;
          currentPage: number;
          pageSize: number;
          totalCount: number;
          items: readonly Schema<'ClinicianAvailabilityInfo'>[];
        }>(apiUrl`${config}/clinicians?${params.toString()}`)          .pipe(cancelSource.token.asTakeUntil()));
        this.#searchResults$.set({
          totalCount: data.totalCount,
          totalPages: data.totalPages,
          currentPage: data.currentPage,
          filterUsed: filter,
          pageSize: data.pageSize,
          items: data.items,
        });
        this.#piwik.trackEvent('directory', 'clinician-total', {subKey: 'in-network', type: data.totalCount});
        this.#cancellationToken = null;
        span.setStatus({
          code: 1, // 1 = OK
        });
      } catch (e) {
        this.#cancellationToken = null;
        if ((e instanceof Error && e.name === 'AbortError') || e instanceof OperationCancelledError) {
          span.setStatus({
            code: 0, // 0 = UNSET
            message: 'Search cancelled due to new search request',
          });
          return;
        }
        const error = this.#app.errorOccurred(e);
        this.#searchResults$.update(
          results => ({
            ...(results ?? {
              totalPages: 0,
              totalCount: 0,
              currentPage: 0,
              pageSize: 0,
            }),
            filterUsed: filter,
            items: [],
            error,
          }));
        span.setStatus({
          code: 2, // 2 = ERROR
        })
      } finally {
        this.#searching$.set(false);
      }
    });

  }, 150);


}

export interface SearchPaging {
  readonly page: number;
  readonly pageSize: number;
}

export interface SearchFilter {
  readonly location?: Schema<'Coordinates'>
  readonly weeksOut?: 0 | 1 | 2 | 4;
  readonly typeOfCare?: ReasonType;
  readonly setting?: AppointmentSettingType;
  readonly genderId?: number;
  readonly languageId?: number;
  readonly clinician?: string;
  readonly ageRange?: 'Children' | 'Adult' | 'Senior';
  readonly state: State;
  readonly timeOfDay?: TimeOfDay;
  readonly dayOfWeek?: Day;
  readonly insuranceId?: number;
  readonly insurancePlanId?: number;
  readonly conditionId?: number;
}
